import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ViewResaleInterestModal from './ViewResaleInterestModal';

const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (Number.isNaN(number)) return '';
    return number.toLocaleString('en-US');
};

const getStatusLabel = (status) => {
    if (status === 'contacted') return 'Contacted';
    if (status === 'closed') return 'Closed';
    if (status === 'cancelled') return 'Cancelled';
    return 'Pending';
};

const getStatusClass = (status) => {
    if (status === 'contacted') return 'info';
    if (status === 'closed') return 'success';
    if (status === 'cancelled') return 'danger';
    return 'warning';
};

const ResaleInterestsTable = () => {
    const [interests, setInterests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentInterest, setCurrentInterest] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const fetchInterests = async () => {
        setLoading(true);
        try {
            const res = await api.get('/resaleInterests/unpaginated');
            setInterests(res.data.response?.data || []);
        } catch (error) {
            console.error('Error fetching resale interests:', error);
            toast.error(error?.response?.data?.message || 'Failed to load the interest requests.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInterests();
    }, []);

    const columns = [
        {
            name: 'Request No.',
            selector: (row) => row?.id,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Buyer Name',
            selector: (row) => row?.buyerName || '',
            sortable: true,
            minWidth: '150px',
        },
        {
            name: 'Buyer Number',
            selector: (row) => row?.buyerMobile || '',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Unit',
            selector: (row) => (row?.resale_unit?.id ? `#${row.resale_unit.id}` : ''),
            sortable: true,
            width: '90px',
        },
        {
            name: 'Developer',
            selector: (row) => row?.resale_unit?.developerName || '',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Project',
            selector: (row) => row?.resale_unit?.projectName || '',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Cash To Seller',
            selector: (row) => Number(row?.resale_unit?.paidAmount ?? 0),
            format: (row) => formatMoney(row?.resale_unit?.paidAmount),
            sortable: true,
            minWidth: '130px',
        },
        {
            name: 'Buyer Cash',
            selector: (row) => Number(row?.availableCash ?? 0),
            format: (row) => formatMoney(row?.availableCash),
            sortable: true,
            minWidth: '120px',
        },
        {
            name: 'Buyer Installment',
            selector: (row) => Number(row?.comfortableInstallment ?? 0),
            format: (row) => formatMoney(row?.comfortableInstallment),
            sortable: true,
            minWidth: '150px',
        },
        {
            name: 'Status',
            cell: (row) => (
                <span
                    className={`bg-${getStatusClass(row?.status)}-focus text-${getStatusClass(
                        row?.status
                    )}-main px-24 py-4 rounded-pill fw-medium text-sm`}
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {getStatusLabel(row?.status)}
                </span>
            ),
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Received',
            selector: (row) => (row?.created_at ? String(row.created_at).split('T')[0] : ''),
            sortable: true,
            minWidth: '120px',
        },
        {
            name: 'Actions',
            cell: (row) => (
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                        setCurrentInterest(row);
                        setShowViewModal(true);
                    }}
                >
                    View
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '90px',
        },
    ];

    const filteredData = useMemo(
        () =>
            interests.filter((item) => {
                const search = searchText.toLowerCase();
                const matchesSearch =
                    !searchText ||
                    String(item?.id ?? '').includes(search) ||
                    (item?.buyerName || '').toLowerCase().includes(search) ||
                    (item?.buyerMobile || '').toLowerCase().includes(search) ||
                    (item?.buyerEmail || '').toLowerCase().includes(search) ||
                    (item?.resale_unit?.developerName || '').toLowerCase().includes(search) ||
                    (item?.resale_unit?.projectName || '').toLowerCase().includes(search) ||
                    String(item?.resale_unit?.id ?? '').includes(search);

                const matchesStatus = selectedStatus ? item?.status === selectedStatus : true;

                return matchesSearch && matchesStatus;
            }),
        [interests, searchText, selectedStatus]
    );

    const csvHeaders = [
        { label: 'Request No.', key: 'id' },
        { label: 'Buyer Name', key: 'buyerName' },
        { label: 'Buyer Number', key: 'buyerMobile' },
        { label: 'Buyer Email', key: 'buyerEmail' },
        { label: 'Available Cash', key: 'availableCash' },
        { label: 'Comfortable Installment', key: 'comfortableInstallment' },
        { label: 'Message', key: 'message' },
        { label: 'Resale Unit', key: 'resale_unit.id' },
        { label: 'Developer', key: 'resale_unit.developerName' },
        { label: 'Project', key: 'resale_unit.projectName' },
        { label: 'Cash To Seller', key: 'resale_unit.paidAmount' },
        { label: 'Status', key: 'status' },
        { label: 'Internal Notes', key: 'adminNotes' },
        { label: 'Received At', key: 'created_at' },
    ];

    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };

    const pendingCount = interests.filter((item) => item?.status === 'pending').length;

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <input
                            type="text"
                            placeholder="Search requests..."
                            className="form-control"
                            style={{ maxWidth: '260px' }}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <select
                            className="form-select"
                            style={{ maxWidth: '220px' }}
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            <option value="pending">Pending ({pendingCount})</option>
                            <option value="contacted">Contacted</option>
                            <option value="closed">Closed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <button className="btn btn-outline-secondary" onClick={fetchInterests} disabled={loading}>
                            Refresh
                        </button>
                    </div>
                    <CSVLink
                        data={filteredData}
                        headers={csvHeaders}
                        filename={`resale-interests-${selectedStatus || 'all'}.csv`}
                        className="btn btn-secondary"
                        target="_blank"
                    >
                        Export CSV
                    </CSVLink>
                </div>

                <div className="table-responsive">
                    <DataTable
                        columns={columns}
                        data={filteredData}
                        progressPending={loading}
                        pagination
                        highlightOnHover
                        responsive
                        striped
                        customStyles={customStyles}
                        noDataComponent={loading ? '' : <div className="p-4 text-center">No requests found.</div>}
                        paginationComponentOptions={{
                            rowsPerPageText: 'Rows per page:',
                            rangeSeparatorText: 'of',
                            selectAllRowsItem: true,
                            selectAllRowsItemText: 'All',
                        }}
                    />
                </div>
            </div>

            {showViewModal && currentInterest && (
                <ViewResaleInterestModal
                    show={showViewModal}
                    handleClose={() => {
                        setShowViewModal(false);
                        setCurrentInterest(null);
                    }}
                    interest={currentInterest}
                    onUpdated={fetchInterests}
                />
            )}
        </div>
    );
};

export default ResaleInterestsTable;
